import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'

import {
  Button,
  Icon,
  SafeHtmlContent,
  Text
} from '@yes.technology/react-toolkit'

import 'asset/css/form.css'
import { ActivityModelDraw } from 'types/shared'
import { ActivityInputProps } from '../Activity.types'
import {
  Agreement,
  Container,
  ImageContainer,
  LabelArea
} from './ActivityInputDraw.styles'

import { interpolateTerms, type Parameters } from 'utils/interpolate'
import TextSignatureEditor, {
  TextSignatureEditorRef
} from './TextSignatureEditor'
import CanvasSignatureEditor, {
  CanvasSignatureEditorRef
} from './CanvasSignatureEditor'
import useDataFromWorkflow from 'modules/interaction/hooks/useDataFromWorkflow'

export type ActivityInputDrawProps = {
  explanation?: string
  label?: string
  informational?: boolean
  inputValue: string

  onChange: ActivityInputProps['handleChange']
} & Pick<ActivityModelDraw, 'agreement' | 'data_container_variable'>

const parseDrawValue = (value: string) => {
  try {
    return JSON.parse(value)
  } catch (error) {
    return {}
  }
}

function ActivityInputDraw({
  explanation,
  inputValue,
  label,
  agreement,
  informational,
  data_container_variable,
  onChange
}: ActivityInputDrawProps) {
  const { t } = useTranslation()

  const values: Parameters = parseDrawValue(inputValue)

  const { base64 = '', ...params } = values

  const [mode, setMode] = useState<'text-editor' | 'canvas-editor' | 'preview'>(
    base64 || informational ? 'preview' : 'canvas-editor'
  )

  const canvasEditorRef = useRef<CanvasSignatureEditorRef>(null)
  const textEditorRef = useRef<TextSignatureEditorRef>(null)

  const { getVariableFromProcessState } = useDataFromWorkflow()

  const handleChange = (lastEditedImageData: string) => {
    onChange(JSON.stringify({ ...params, base64: lastEditedImageData }))
  }

  const handleClear = () => {
    onChange(JSON.stringify({ ...params, base64: '' }))
    canvasEditorRef.current?.clear()
    textEditorRef.current?.clear()
  }

  const text =
    agreement ||
    (data_container_variable &&
      getVariableFromProcessState(data_container_variable)) ||
    ''
  const interpolatedTerms = interpolateTerms(text, params)

  return (
    <Container>
      {explanation && (
        <Text variant='content-04' as='div'>
          {explanation}
        </Text>
      )}

      {interpolatedTerms && (
        <Agreement>
          <SafeHtmlContent html={interpolatedTerms} />
        </Agreement>
      )}

      <div>
        <LabelArea>
          <Text variant='label-required-03' as='label'>
            {label}
          </Text>
          {!informational && (
            <>
              <Button
                variant={mode === 'text-editor' ? 'primary' : 'secondary'}
                onClick={() => setMode('text-editor')}
                title={t('activity-input-draw.type-signature')}
                heightClass='small'
                aria-pressed={mode === 'text-editor'}
              >
                <Icon iconName='Text' icSize='small' />
              </Button>

              <Button
                variant={mode === 'canvas-editor' ? 'primary' : 'secondary'}
                onClick={() => setMode('canvas-editor')}
                title={t('activity-input-draw.draw-signature')}
                heightClass='small'
                aria-pressed={mode === 'canvas-editor'}
              >
                <Icon iconName='SignatureFill' icSize='small' />
              </Button>

              <Button
                variant='secondary'
                onClick={handleClear}
                title={t('activity-input-draw.clear')}
                heightClass='small'
                disabled={!base64}
              >
                <Icon iconName='Cleaner' icSize='small' />
              </Button>
            </>
          )}
        </LabelArea>

        <TextSignatureEditor
          onChange={handleChange}
          enabled={mode === 'text-editor'}
          base64={base64}
          ref={textEditorRef}
        />

        <CanvasSignatureEditor
          enabled={mode === 'canvas-editor'}
          onChange={handleChange}
          ref={canvasEditorRef}
          {...{ base64 }}
        />

        {base64 && mode === 'preview' && (
          <ImageContainer>
            <img src={base64} data-testid='signature-img' />
          </ImageContainer>
        )}
      </div>
    </Container>
  )
}

export default ActivityInputDraw
